import { useMemo, useState } from "react";
import RichTextViewer from "../../../../library/ckeditor/rich-text-viewer";

interface Props {
    isShow: boolean;
    currentNotes?: string | null;
    proposedNotes?: string | null;
}

type ChangeType = "changed" | null;

const normalizeRichText = (value?: string | null): string => {
    if (!value) return "";

    return value
        .replace(/>\s+</g, "><")
        .replace(/\s+/g, " ")
        .trim();
};

const isRichTextEmpty = (value?: string | null): boolean => {
    if (!value) return true;

    const text = value
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/gi, " ")
        .replace(/\s+/g, " ")
        .trim();

    return text === "";
};

const getChangeType = ( currentNotes?: string | null, proposedNotes?: string | null ): ChangeType => {
    return normalizeRichText(currentNotes) !== normalizeRichText(proposedNotes) ? "changed" : null;
};

const ChangeBadge = ({ type }: { type: ChangeType }) => {
    if (!type) return null;

    return (
        <span className="inline-flex items-center rounded-full border border-[#b9d8ef] bg-[#e3f1fa] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[#186cb2]">
            Changed
        </span>
    );
};

const NotesContent = ({value, isView}: {value?: string | null; isView: boolean}) => {
    const content = value ?? "";

    if (isRichTextEmpty(content)) {
        return (
            <div className="rounded border border-slate-200 bg-slate-50 px-3 py-3">
                <span className="text-[10px] text-slate-400">
                    {isView ? "No content available." : "No notes provided."}
                </span>
            </div>
        );
    }

    return (
        <div className="text-[10px]">
            <RichTextViewer value={content} isView={isView} />
        </div>
    );
};

export default function ProposedAmendNotes({
    isShow,
    currentNotes,
    proposedNotes,
}: Props) {
    const [showContent, setShowContent] = useState(true);

    const changeType = useMemo(
        () => getChangeType(currentNotes, proposedNotes),
        [currentNotes, proposedNotes],
    );

    const hasChanges = changeType !== null;

    if (!isShow || !hasChanges) {
        return null;
    }

    return (
        <div className="grid grid-cols-1 gap-3.5">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                {/* SECTION HEADER */}
                <button
                    type="button"
                    onClick={() => setShowContent((previous) => !previous)}
                    className="flex w-full items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2.5 text-left transition-colors hover:bg-slate-50"
                >
                    <div className="flex min-w-0 items-center gap-2">
                        <svg viewBox="0 0 24 24" fill="none" className={`h-3.5 w-3.5 shrink-0 text-[#186cb2] transition-transform ${showContent ? "rotate-0" : "-rotate-90"}`} aria-hidden="true" >
                            <path d="m7 10 5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                            Notes
                        </h3>
                    </div>
                    <span className="shrink-0 text-[9px] font-medium text-slate-400">
                        {showContent ? "Hide" : "Show"}
                    </span>
                </button>

                {showContent && (
                    <>
                        {/* CURRENT / PROPOSED HEADER */}
                        <div className="grid grid-cols-1 border-b border-slate-200 sm:grid-cols-2">
                            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 sm:border-b-0 sm:border-r">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    Current
                                </h3>
                            </div>
                            <div className="bg-[#f5f9fc] px-4 py-2.5">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[#186cb2]">
                                    Proposed
                                </h3>
                            </div>
                        </div>
                        {/* CONTENT */}
                        <div className="grid grid-cols-1 sm:grid-cols-2">
                            {/* CURRENT */}
                            <div className="min-w-0 border-b border-slate-200 p-4 sm:border-b-0 sm:border-r">
                                <div className="mb-2 flex items-center justify-between gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                        Notes
                                    </span>
                                    <ChangeBadge type={changeType} />
                                </div>
                                <NotesContent value={currentNotes} isView />
                            </div>
                            {/* PROPOSED */}
                            <div className="min-w-0 p-4">
                                <div className="mb-2 flex items-center justify-between gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-[#186cb2]">
                                        Notes
                                    </span>
                                    <ChangeBadge type={changeType} />
                                </div>
                                <NotesContent value={proposedNotes} isView />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}